import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Play, ArrowRight, Music } from 'lucide-react'
import { TRACKS } from '../data'

export default function MusicSection({ limit = 4 }: { limit?: number }) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const tracks = TRACKS.slice(0, limit)

  return (
    <section style={{ padding: 'clamp(48px, 8vw, 80px) clamp(16px, 5vw, 32px)', background: 'var(--ink-2)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: '48px',
            paddingBottom: '20px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            <div className="section-label">Releases & Mixes</div>
            <div className="section-title">Music</div>
          </div>
          <Link
            to="/music"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '10px',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              color: 'var(--gold-dim)',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              borderBottom: '1px solid rgba(122,101,64,0.4)',
              paddingBottom: '2px',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--gold)')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--gold-dim)')}
          >
            All Music <ArrowRight size={12} />
          </Link>
        </div>

        {/* Music Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '20px',
          }}
        >
          {tracks.map((track) => (
            <div
              key={track.id}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredId(track.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* Album art */}
              <div
                style={{
                  aspectRatio: '1',
                  background: 'linear-gradient(135deg, #1c1710, #2e2318)',
                  marginBottom: '12px',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Diagonal texture */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `repeating-linear-gradient(
                      45deg,
                      transparent,
                      transparent 12px,
                      rgba(200,169,110,0.04) 12px,
                      rgba(200,169,110,0.04) 13px
                    )`,
                  }}
                />

                {/* TODO: Replace with real cover art:
                    <img src={track.coverArt} alt={track.title} style={{
                      position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover'
                    }} />
                */}

                {/* Music note placeholder */}
                <Music
                  size={32}
                  style={{
                    color: 'rgba(200,169,110,0.15)',
                    position: 'relative',
                    zIndex: 1,
                    transition: 'opacity 0.2s',
                    opacity: hoveredId === track.id ? 0 : 1,
                  }}
                />

                {/* Play button overlay */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    opacity: hoveredId === track.id ? 1 : 0,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '50%',
                      background: 'var(--gold)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Play size={20} fill="var(--ink)" color="var(--ink)" style={{ marginLeft: '2px' }} />
                  </div>
                </div>

                {/* Hover dark overlay */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    opacity: hoveredId === track.id ? 1 : 0,
                    transition: 'opacity 0.2s',
                  }}
                />
              </div>

              {/* Track info */}
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 500,
                  fontSize: '13px',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: hoveredId === track.id ? 'var(--gold)' : 'var(--cream)',
                  marginBottom: '4px',
                  transition: 'color 0.2s',
                }}
              >
                {track.title}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{track.subtitle}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
